// components/MPPayments.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, AutoComplete, Input, Button, message, Tooltip } from "antd";
import { Link } from "react-router-dom"; // se estiver usando React Router

export default function MPPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);

  const maxLength = 25; // máximo de caracteres visíveis
  const ellipsis = (text) =>
    text && text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

  // 🔹 Buscar pagamentos do backend
  const fetchPayments = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        "https://gestor.bebilogo.com.br/api-mercadopago/mp-payments/list"
      );
      setPayments(response.data);
    } catch (err) {
      console.error("Erro ao carregar pagamentos:", err);
      setError("Erro ao carregar pagamentos. Servidor fora do ar.");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    const interval = setInterval(fetchPayments, 15000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return Number(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const getStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "approved":
        return "#d4edda";
      case "pending":
        return "#fff3cd";
      case "rejected":
        return "#f8d7da";
      default:
        return "#f9f9f9";
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    if (!value) return setOptions([]);

    const filtered = payments
      .filter(
        (p) =>
          p.id.toString().includes(value) ||
          (p.payer_email || "").toLowerCase().includes(value.toLowerCase())
      )
      .map((p) => ({
        value: p.id.toString(),
        label: `${p.id} - ${p.payer_email || "Sem email"}`,
      }));

    setOptions(filtered);
  };

  const onSelect = (value) => setSearchValue(value);

  const filteredPayments = payments.filter(
    (p) =>
      p.id.toString().includes(searchValue) ||
      (p.payer_email || "").toLowerCase().includes(searchValue.toLowerCase())
  );

const updatePayment = async (id) => {
  try {
    const response = await axios.get(
      `https://gestor.bebilogo.com.br/api-mercadopago/mp-payments/update/${id}`
    );

    message.success(`Pagamento ${id} atualizado com dados do Mercado Pago!`);

    setPayments((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, ...response.data.payment } : p
      )
    );
  } catch (err) {
    console.error("Erro ao atualizar pagamento:", err);
    message.error(`Erro ao atualizar pagamento ${id}`);
  }
};


  // ...imports e hooks permanecem iguais

const cancelPayment = async (id) => {
  try {
    const response = await axios.post(
      "https://gestor.bebilogo.com.br/api-mercadopago/mp-payments/cancel-payment",
      { paymentId: id }
    );
    if (response.data.success) {
      message.success(`Pagamento ${id} cancelado!`);
      // Atualiza status localmente
      setPayments((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, status: "cancelled" } : p
        )
      );
    }
  } catch (err) {
    console.error(err);
    message.error(`Erro ao cancelar pagamento ${id}`);
  }
};

// 🔹 Colunas da tabela
const columns = [
  {
    title: "ID Pagamento",
    dataIndex: "id",
    key: "id",
    render: (v) => <Tooltip title={v}>{ellipsis(v.toString())}</Tooltip>,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (v) => <Tooltip title={v}>{ellipsis(v)}</Tooltip>,
  },
  {
    title: "Tipo",
    dataIndex: "payment_type_id",
    key: "payment_type_id",
    render: (v) => <Tooltip title={v}>{ellipsis(v)}</Tooltip>,
  },
  {
    title: "Mtodo",
    dataIndex: "payment_method_id",
    key: "payment_method_id",
    render: (v) => <Tooltip title={v}>{ellipsis(v)}</Tooltip>,
  },
  {
    title: "Valor",
    dataIndex: "transaction_amount",
    key: "transaction_amount",
    render: (v) => formatCurrency(v),
  },
  {
    title: "Parcelas",
    dataIndex: "installments",
    key: "installments",
    render: (v) => v || 1,
  },
  {
    title: "Email",
    dataIndex: "payer_email",
    key: "payer_email",
    render: (v) => <Tooltip title={v}>{ellipsis(v)}</Tooltip>,
  },
  {
    title: "Criado em",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (v) => (v ? new Date(v).toLocaleString("pt-BR") : "-"),
  },
  {
    title: "Ticket",
    dataIndex: "ticket_url",
    key: "ticket_url",
    render: (v) =>
      v ? (
        <Button
          type="link"
          onClick={() => window.open(v, "_blank")}
          style={{ padding: 0 }}
        >
          Abrir
        </Button>
      ) : (
        "-"
      ),
  },
  {
    title: "Ações",
    key: "actions",
    render: (_, record) => (
      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
        <Button type="primary" onClick={() => updatePayment(record.id)}>
          Atualizar
        </Button>

        {(record.status === "in_process" || record.status === "pending") && (
          <Button type="danger" onClick={() => cancelPayment(record.id)}>
            Cancelar
          </Button>
        )}
      </div>
    ),
  },
];

  return (
  <div style={{ padding: "70px 20px" }}>
  <h2>Pagamentos Mercado Pago</h2>

  {/* Linha de busca + botão */}
  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: 20 }}>
    <AutoComplete
      popupMatchSelectWidth={252}
      style={{ width: 350 }}
      value={searchValue}
      options={options}
      onSelect={onSelect}
      onSearch={handleSearch}
      size="large"
    >
      <Input.Search size="large" placeholder="Buscar por ID ou Email" enterButton />
    </AutoComplete>

    <Link to="/webhooks-mp">
      <Button type="primary" size="large">
        Webhooks
      </Button>
    </Link>
  </div>

      {loading && <p>Carregando...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {!loading && filteredPayments.length === 0 && !error && (
        <p>Nenhum pagamento encontrado.</p>
      )}

      <Table
        rowKey="id"
        columns={columns}
        size="small"
        dataSource={filteredPayments}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
  <div
    style={{
      width: "100%",
      overflowX: "auto",
    }}
  >
    <pre
      style={{
        display: "block",
        maxWidth: "100%",
        background: "#f0f0f0",
        padding: "10px",
        borderRadius: "5px",
        whiteSpace: "pre-wrap", // mantém indentação e quebra quando precisar
        wordBreak: "break-word",
        margin: 0,
      }}
    >
      {JSON.stringify(record, null, 2)}
    </pre>
  </div>
),
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [record.id] : []);
          },
        }}
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
          onClick: () => {
            const isExpanded = expandedRowKeys.includes(record.id);
            setExpandedRowKeys(isExpanded ? [] : [record.id]);
          },
          style: { cursor: "pointer", background: getStatusColor(record.status) },
        })}
      />
    </div>
  );
}
